import { ResourcePage } from "@/components/ResourcePage";
import { ExternalLink } from "lucide-react";
import { TAX_DUE_DATES_2026, TAX_RATES_2025, TAX_RETENTION_GUIDE, TAX_GLOSSARY, TAXPAYER_RIGHTS, TAX_FORMS, IRS_PUBLICATIONS } from "@/lib/taxContent";
import useReveal from "@/lib/useReveal";

export function TaxDueDates() {
  useReveal();
  return (
    <ResourcePage title="Tax Due Dates: 2026" testId="page-tax-due-dates">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">
        Federal filing and payment deadlines for the 2026 calendar year. Always confirm against the IRS site for current-year specifics.
      </p>
      <div className="space-y-8">
        {TAX_DUE_DATES_2026.map((m) => (
          <div key={m.month} className="reveal" data-testid={`tdd-${m.month.toLowerCase()}`}>
            <h2 className="font-serif-display text-2xl md:text-3xl text-navy-800 mb-4 pb-2 border-b border-gold-200">{m.month}</h2>
            <ul className="divide-y divide-navy-50 rounded border border-navy-100 bg-white overflow-hidden">
              {m.items.map((it, i) => (
                <li key={i} className="grid grid-cols-[64px_1fr] gap-4 px-5 py-4">
                  <div className="font-serif-display text-2xl text-gold-500 tabular-nums leading-none pt-1">{it.day}</div>
                  <div>
                    <div className="font-medium text-navy-800 text-[15px]">{it.title}</div>
                    <div className="text-navy-600 text-[14px] mt-1 leading-relaxed">{it.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </ResourcePage>
  );
}

export function TaxRates() {
  useReveal();
  const Bracket = ({ title, rows, testId }) => (
    <div className="reveal rounded border border-navy-100 bg-white overflow-hidden" data-testid={testId}>
      <div className="px-5 py-3 bg-navy-800 text-cream-50 font-serif-display text-lg">{title}</div>
      <table className="w-full text-[14px]">
        <thead className="bg-navy-50 text-navy-700"><tr><th className="text-left px-5 py-2 font-medium">Rate</th><th className="text-left px-5 py-2 font-medium">From</th><th className="text-left px-5 py-2 font-medium">To</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (<tr key={i} className="border-t border-navy-50"><td className="px-5 py-2 font-semibold text-navy-800 tabular-nums">{r[0]}</td><td className="px-5 py-2 tabular-nums">{r[1]}</td><td className="px-5 py-2 tabular-nums">{r[2]}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
  return (
    <ResourcePage title="Tax Rates: 2025" testId="page-tax-rates">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">2025 federal income tax brackets, standard deductions, long-term capital gains rates, and key contribution limits.</p>
      <div className="grid gap-6 md:grid-cols-2">
        <Bracket title="Single" rows={TAX_RATES_2025.brackets.single} testId="rates-single" />
        <Bracket title="Married Filing Jointly" rows={TAX_RATES_2025.brackets.mfj} testId="rates-mfj" />
        <Bracket title="Head of Household" rows={TAX_RATES_2025.brackets.hoh} testId="rates-hoh" />
        <div className="rounded border border-navy-100 bg-white overflow-hidden reveal" data-testid="rates-std-ltcg">
          <div className="px-5 py-3 bg-navy-800 text-cream-50 font-serif-display text-lg">Standard Deduction</div>
          <ul className="divide-y divide-navy-50 text-[14px]">
            {Object.entries(TAX_RATES_2025.standardDeduction).map(([k, v]) => (<li key={k} className="flex justify-between px-5 py-2.5"><span className="text-navy-600">{k.toUpperCase()}</span><span className="font-semibold tabular-nums text-navy-800">{v}</span></li>))}
          </ul>
          <div className="px-5 py-3 bg-navy-800 text-cream-50 font-serif-display text-lg">Long-Term Capital Gains</div>
          <ul className="divide-y divide-navy-50 text-[14px]">
            {TAX_RATES_2025.ltcg.map(([rate, range], i) => (<li key={i} className="flex justify-between gap-3 px-5 py-2.5"><span className="font-semibold tabular-nums text-navy-800">{rate}</span><span className="text-navy-600 text-right">{range}</span></li>))}
          </ul>
        </div>
        <div className="md:col-span-2 rounded border border-navy-100 bg-white overflow-hidden reveal" data-testid="rates-contrib">
          <div className="px-5 py-3 bg-navy-800 text-cream-50 font-serif-display text-lg">2025 Retirement & HSA Contribution Limits</div>
          <ul className="divide-y divide-navy-50 text-[14px] grid grid-cols-1 md:grid-cols-2">
            {Object.entries(TAX_RATES_2025.retirementContribLimits).map(([k, v]) => (<li key={k} className="flex justify-between px-5 py-2.5"><span className="text-navy-600">{k}</span><span className="font-semibold tabular-nums text-navy-800">{v}</span></li>))}
          </ul>
        </div>
      </div>
    </ResourcePage>
  );
}

export function TaxRetentionGuide() {
  useReveal();
  return (
    <ResourcePage title="Tax Retention Guide" testId="page-tax-retention">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">General guidance on how long to keep tax records. Specific situations can require longer, so when in doubt, ask David.</p>
      <div className="rounded border border-navy-100 bg-white overflow-hidden reveal">
        <table className="w-full text-[14.5px]">
          <thead className="bg-navy-800 text-cream-50"><tr><th className="text-left px-5 py-3">Record</th><th className="text-left px-5 py-3 w-[40%]">Keep for</th></tr></thead>
          <tbody>{TAX_RETENTION_GUIDE.map((r, i) => (<tr key={i} className="border-t border-navy-50 odd:bg-cream-100/30"><td className="px-5 py-3 text-navy-700">{r.record}</td><td className="px-5 py-3 font-semibold text-navy-800">{r.keep}</td></tr>))}</tbody>
        </table>
      </div>
    </ResourcePage>
  );
}

export function TaxGlossary() {
  useReveal();
  return (
    <ResourcePage title="Tax Glossary" testId="page-tax-glossary">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">Plain-language definitions of common tax terms.</p>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
        {TAX_GLOSSARY.map((g) => (
          <div key={g.term} className="py-4 border-b border-navy-100 reveal" data-testid={`glossary-${g.term.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <dt className="font-serif-display text-[19px] text-navy-800 font-semibold">{g.term}</dt>
            <dd className="text-navy-600 text-[14.5px] leading-relaxed mt-1">{g.def}</dd>
          </div>
        ))}
      </dl>
    </ResourcePage>
  );
}

export function TaxpayerRights() {
  useReveal();
  return (
    <ResourcePage title="Taxpayer Bill of Rights" testId="page-taxpayer-rights">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">Every taxpayer has fundamental rights when interacting with the IRS. Below is a summary of the IRS Taxpayer Bill of Rights.</p>
      <ol className="space-y-5 counter-reset:[item]">
        {TAXPAYER_RIGHTS.map((r, i) => (
          <li key={i} className="flex gap-5 reveal" data-testid={`right-${i}`}>
            <div className="font-serif-display text-3xl text-gold-500 tabular-nums w-10 shrink-0">{i + 1}</div>
            <div>
              <div className="font-serif-display text-[20px] text-navy-800 font-semibold">{r.title}</div>
              <div className="text-navy-600 text-[15px] leading-relaxed mt-1">{r.body}</div>
            </div>
          </li>
        ))}
      </ol>
    </ResourcePage>
  );
}

export function TaxForms() {
  useReveal();
  return (
    <ResourcePage title="Tax Forms" testId="page-tax-forms">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">Quick access to the most commonly used IRS tax forms. Opens directly from irs.gov.</p>
      <div className="space-y-10">
        {TAX_FORMS.map((cat) => (
          <div key={cat.category} className="reveal">
            <h2 className="font-serif-display text-2xl text-navy-800 mb-4 pb-2 border-b border-gold-200">{cat.category}</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cat.forms.map((f) => (
                <li key={f.id}>
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="card-lift flex items-center justify-between gap-3 px-5 py-4 rounded border border-navy-100 bg-white" data-testid={`form-${f.id.replace(/\s/g, "-")}`}>
                    <div><div className="font-medium text-navy-800 text-[14.5px]">{f.name}</div><div className="text-[12px] text-navy-400 mt-0.5">{f.id}</div></div>
                    <ExternalLink size={16} className="text-gold-500 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </ResourcePage>
  );
}

export function IRSPublications() {
  useReveal();
  return (
    <ResourcePage title="IRS Publications" testId="page-irs-publications">
      <p className="max-w-3xl text-navy-600 text-[16px] leading-relaxed mb-10">Curated list of frequently referenced IRS publications. Opens directly from irs.gov.</p>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {IRS_PUBLICATIONS.map((p) => (
          <li key={p.id}>
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="card-lift flex items-center justify-between gap-3 px-5 py-4 rounded border border-navy-100 bg-white" data-testid={`pub-${p.id.replace(/\s/g, "-")}`}>
              <div><div className="font-medium text-navy-800 text-[14.5px]">{p.name}</div><div className="text-[12px] text-navy-400 mt-0.5">{p.id}</div></div>
              <ExternalLink size={16} className="text-gold-500 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </ResourcePage>
  );
}

export function WheresMyRefund() {
  useReveal();
  return (
    <ResourcePage title="Where Is My Refund?" testId="page-wheres-my-refund">
      <div className="max-w-3xl space-y-6 text-navy-600 text-[16px] leading-relaxed reveal">
        <p>The fastest way to check the status of your federal income tax refund is the IRS's official <strong>Where's My Refund?</strong> tool. You'll need:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Your <strong>Social Security number</strong> or ITIN</li>
          <li>Your <strong>filing status</strong></li>
          <li>The <strong>exact whole-dollar refund amount</strong> from your return</li>
        </ul>
        <p>The tool updates once daily, usually overnight. Most refunds are issued in less than 21 days for e-filed returns.</p>
        <a href="https://www.irs.gov/refunds" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex" data-testid="refund-irs-link">
          Open IRS "Where's My Refund?" <ExternalLink size={14} />
        </a>
        <div className="mt-8 rounded border border-navy-100 bg-cream-100/40 p-5 text-[14.5px] text-navy-700">
          <strong className="text-navy-800">Oklahoma state refund?</strong> Visit{" "}
          <a className="underline decoration-gold-400/60" href="https://oklahoma.gov/tax/individuals/find-the-status-of-my-refund.html" target="_blank" rel="noopener noreferrer">oklahoma.gov/tax</a>.
        </div>
      </div>
    </ResourcePage>
  );
}

export function IRSWithholding() {
  useReveal();
  return (
    <ResourcePage title="IRS Tax Withholding Estimator" testId="page-withholding">
      <div className="max-w-3xl space-y-5 text-navy-600 text-[16px] leading-relaxed reveal">
        <p>If you are an employee, the IRS Tax Withholding Estimator can help you determine whether you need to give your employer a new <strong>Form W-4</strong> to avoid having too much or too little Federal income tax withheld from your pay.</p>
        <h2 className="font-serif-display text-2xl text-navy-800 pt-2">Who can benefit?</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Employees who would like to change their withholding to reduce their tax refund or balance due.</li>
          <li>Employees with concurrent jobs, dual-income couples, head of household filers, or those eligible for the Child Tax Credit.</li>
          <li>Employees with non-wage income (e.g., investment, freelance) who would prefer to have it covered through paycheck withholding.</li>
        </ul>
        <h2 className="font-serif-display text-2xl text-navy-800 pt-2">Tips before you start</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Have your most recent pay stubs handy.</li>
          <li>Have your most recent income tax return handy.</li>
          <li>Estimate values if necessary. Results are only as accurate as the inputs.</li>
        </ul>
        <div className="flex flex-wrap gap-3 pt-4">
          <a href="https://www.irs.gov/individuals/tax-withholding-estimator" target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="withholding-irs-link">
            Open IRS Withholding Estimator <ExternalLink size={14} />
          </a>
          <a href="https://www.irs.gov/pub/irs-pdf/fw4.pdf" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="withholding-w4">
            Download Form W-4 (PDF)
          </a>
        </div>
      </div>
    </ResourcePage>
  );
}
